import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { singleUnitName } from 'helpers/other';
import { TextFormattedNumber } from 'components';
import { imgBaseUrl } from 'configs';
import round from 'helpers/mathRound';
import { extractAttributesValues } from 'pages/quotes/helpers';
import { useStyles } from '../styles';
import logo from '../../../components/Logo/logo-dermpro.svg';
import { calculateTotals } from '../helpers';


const attributesToFind = [
  'short_description',
  'additional_subscription_price',
  'quotepro_discounts',
  'image',
  'subscription_options',
  'subscription_active',
  'catalog_product_unit',
  'savings',
  'subscription_label'
];

const QuoteAutomationItemsTableRow = ({ item }) => {
  const classes = useStyles();
  const [loaded, setLoaded] = useState(false);

  const { quoteProducts } = useSelector((state: IReduxState) => state.quote);
  const { stores = [] } = useSelector((state: IReduxState) => state.user.profile);
  const mediaStoreUrl = `${stores[0]?.apiUrl}/media/catalog/product` || imgBaseUrl;

  const {
    sku,
    qty,
    product_option: {
      extension_attributes: {
        subscription_id
      },
    },
    price: updatedPrice,
    custom_subscription_price
  } = item;

  const quoteProductData = quoteProducts?.find((quoteItem) => quoteItem.sku === sku) as IQuoteProduct || {};
  const {
    name,
    price,
    custom_attributes
  } = quoteProductData as IQuoteProduct || {};

  const itemPrice = updatedPrice || price;

  const attributesValues = custom_attributes
    ? extractAttributesValues(custom_attributes, attributesToFind)
    : [];

  const {
    image,
    subscription_options,
    subscription_active,
    catalog_product_unit,
    savings,
    subscription_label,
    additional_subscription_price
  } = attributesValues || {};

  const savingsValue = round(Number(savings)) || 0;
  const isAlleSKU = item.sku.includes('bdsku');
  const unitsName = catalog_product_unit || 'units';
  const subscriptionData = subscription_options?.find((s) => s.id === subscription_id);

  const { skuTotals } = calculateTotals([item]);

  const {
    discount,
    subtotal,
    total
  } = skuTotals[sku];

  useEffect(() => {
    setLoaded(false);
  }, [image]);

  return (
    <TableRow key={item._id}>
      <TableCell width="70px">
        <img
          src={loaded ? `${mediaStoreUrl}${image}` : logo}
          alt={`${name} image`}
          className={classes.quoteSummaryTableImage}
          onLoad={() => setLoaded(true)}
          onError={(e) => {
            (e.target as HTMLImageElement).onerror = null;
            (e.target as HTMLImageElement).src = logo;
          }}
        />
      </TableCell>
      <TableCell width="30%">
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>
          {name}&nbsp;
          <span className="text-13 font-italic font-medium whitespace-no-wrap">
            (<TextFormattedNumber value={round(updatedPrice ? updatedPrice : itemPrice - savingsValue)} prefix="$" />/
            {singleUnitName(unitsName)})
          </span>
        </Typography>
        {subscription_active === '1' && !!subscriptionData?.frequency_count &&
        <>
          <div className="flex flex-wrap">
            <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
              {subscription_label}{':'}
            </span>
            <span className="text-13 whitespace-no-wrap font-italic">
              {subscriptionData?.sproduct_label || ''}
            </span>
          </div>
          <div className="flex flex-wrap mb-2">
            <span className="text-13 whitespace-no-wrap mr-1 font-italic font-bold">
              {'Periodic Price:'}
            </span>
            <span className="text-13 whitespace-no-wrap font-italic">
              <TextFormattedNumber
                value={custom_subscription_price
                  ? custom_subscription_price
                  : round(subscriptionData?.installment_price) || 0
                }
                prefix="$"
              />
            </span>
          </div>
        </>
        }
        <Typography dangerouslySetInnerHTML={{ __html: attributesValues?.short_description || '' }} />
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Quantity</Typography>
        <Typography variant="body2">{`${qty} ${
          qty === 1 ? singleUnitName(unitsName) : unitsName
        }`}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Subtotal</Typography>
        <TextFormattedNumber value={subtotal * qty} prefix="$" />
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>Savings</Typography>
        <TextFormattedNumber value={discount} prefix="$" />
      </TableCell>
      {additional_subscription_price &&
        <TableCell>
          <Typography variant="subtitle2" className="font-semibold" gutterBottom>Monthly Fee</Typography>
          <TextFormattedNumber value={additional_subscription_price} prefix="$" />
        </TableCell>
      }
      <TableCell className="font-semibold">
        <Typography variant="subtitle2" className="font-semibold" gutterBottom>TOTAL</Typography>
        <TextFormattedNumber value={isAlleSKU ? 0 : total} prefix="$" />
      </TableCell>
    </TableRow>
  );
};

export default QuoteAutomationItemsTableRow;
